import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Lock } from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Connexion réussie!');
      navigate('/portail');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Branding */}
      <div 
        className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12"
        style={{ background: 'linear-gradient(135deg, #1D1D1B 0%, #2a2a28 100%)' }}
      >
        <div>
          <Link to="/" className="inline-flex items-center gap-3">
            <img 
              src="/logo-pey.png" 
              alt="PEY Consultants" 
              className="h-10 object-contain brightness-0 invert"
            />
          </Link>
        </div>
        
        <div>
          <h1 className="font-heading text-4xl font-bold text-white mb-4">
            Bienvenue dans votre
            <span 
              className="block mt-1"
              style={{ 
                background: 'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              espace client
            </span>
          </h1>
          <p className="text-white/60 text-lg max-w-md">
            Accédez à vos projets, documents et suivez l'avancement de vos travaux en temps réel.
          </p>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div 
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, rgba(255,213,55,0.2) 0%, rgba(249,180,56,0.2) 100%)' }}
            >
              <Lock className="w-4 h-4 text-[#FFD537]" />
            </div>
            <span className="text-white/60 text-sm">Connexion sécurisée SSL</span>
          </div>
          <p className="text-white/30 text-sm">
            © {new Date().getFullYear()} PEY Consultants inc.
          </p>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex flex-col bg-neutral-50">
        {/* Header */}
        <div className="p-4 lg:p-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-neutral-500 hover:text-[#1D1D1B] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au site
          </Link>
        </div>

        {/* Form */}
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center pb-2">
              {/* Mobile logo */}
              <div className="mb-4 lg:hidden">
                <img 
                  src="/favicon.png" 
                  alt="PEY" 
                  className="w-14 h-14 mx-auto object-contain"
                />
              </div>
              <CardTitle className="font-heading text-2xl text-[#1D1D1B]">Connexion</CardTitle>
              <CardDescription className="text-neutral-500">
                Accédez à votre espace sécurisé
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#1D1D1B]">Courriel</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="vous@exemple.com"
                    className="h-11"
                    data-testid="login-email"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#1D1D1B]">Mot de passe</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    className="h-11"
                    data-testid="login-password"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-11"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Connexion...
                    </>
                  ) : (
                    'Se connecter'
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center text-sm">
                <p className="text-neutral-500">
                  Pas encore de compte?{' '}
                  <Link 
                    to="/inscription" 
                    className="font-semibold hover:underline"
                    style={{ color: '#F9B438' }}
                  >
                    Créer un compte
                  </Link>
                </p>
              </div>

              {/* DG Solutions branding */}
              <div className="mt-6 pt-4 border-t border-neutral-200 text-center">
                <p className="text-xs text-neutral-400">
                  Développé par{' '}
                  <a 
                    href="https://www.dgsolutionscanada.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#F9B438] hover:underline"
                  >
                    DG Solutions
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
